import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PackageCheck, Hammer } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { formatCents, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ESTIMATE_STATUS_BADGE,
  PO_STATUS_BADGE,
  PICKUP_STATUS_BADGE,
  type PickupStatus,
} from '@/lib/badges';
import type { EstimateStatus, POStatus } from '@billflow/shared';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PendingPickup {
  id: number;
  number: string;
  customer_name: string;
  expected_pickup_date: string | null;
  pickup_status: PickupStatus;
  overdue: number; // 0 | 1 (SQLite boolean)
}

interface DueInvoice {
  id: number;
  number: string;
  customer_name: string;
  date: string;
  status: EstimateStatus;
  balance: number;
}

interface DuePO {
  id: number;
  number: string;
  supplier_name: string;
  order_date: string;
  status: POStatus;
  balance: number;
}

interface DashboardData {
  from: string;
  to: string;
  cash_today: number;
  period_revenue: number;
  outstanding_count: number;
  outstanding_total: number;
  cash_on_hand: number;
  supplier_payables: number;
  open_pos_count: number;
  open_pos_total: number;
  pending_pickups: { count: number; overdue: number };
  countertop_jobs_in_progress: number;
  countertop_installs_overdue: number;
  pending_pickup_list: PendingPickup[];
  due_invoices: DueInvoice[];
  due_pos: DuePO[];
}

interface DashboardResponse {
  data: DashboardData;
}

// ─── Stat tile ─────────────────────────────────────────────────────────────────

interface StatTileProps {
  title: string;
  value: string;
  sub?: string;
  /** Tailwind classes for the sub-text (defaults to muted). */
  subClassName?: string;
  icon?: React.ReactNode;
}

function StatTile({ title, value, sub, subClassName, icon }: StatTileProps) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        {sub && <p className={subClassName ?? 'text-xs text-muted-foreground mt-0.5'}>{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard', from, to],
    queryFn: () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return apiFetch<DashboardResponse>(`/api/dashboard${qs ? `?${qs}` : ''}`);
    },
  });

  const d = data?.data;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        {/* Date range */}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Label htmlFor="dash-from" className="text-muted-foreground">
              From
            </Label>
            <Input
              id="dash-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-36 h-8 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="dash-to" className="text-muted-foreground">
              To
            </Label>
            <Input
              id="dash-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-36 h-8 text-sm"
            />
          </div>
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load dashboard'}
        </p>
      )}

      {d && (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {isAdmin && <StatTile title="Sales Today" value={formatCents(d.cash_today)} />}
            {isAdmin && <StatTile title="Period Revenue" value={formatCents(d.period_revenue)} />}
            <StatTile
              title="Outstanding"
              value={formatCents(d.outstanding_total)}
              sub={`${d.outstanding_count} estimate${d.outstanding_count !== 1 ? 's' : ''}`}
            />
            {isAdmin && (
              <StatTile
                title="Cash on Hand"
                value={formatCents(d.cash_on_hand)}
                sub="net cash position"
              />
            )}
            <StatTile title="Supplier Payables" value={formatCents(d.supplier_payables)} />
            <StatTile
              title="Open POs"
              value={formatCents(d.open_pos_total)}
              sub={`${d.open_pos_count} order${d.open_pos_count !== 1 ? 's' : ''}`}
            />
            <StatTile
              title="Pending Pickups"
              value={String(d.pending_pickups.count)}
              icon={<PackageCheck className="h-4 w-4" />}
              sub={`${d.pending_pickups.overdue} overdue`}
              subClassName={
                d.pending_pickups.overdue > 0
                  ? 'text-xs font-medium text-amber-600 mt-0.5'
                  : 'text-xs text-muted-foreground mt-0.5'
              }
            />
            <Link
              to="/countertop-jobs"
              className="block rounded-lg transition-colors hover:bg-muted/30"
            >
              <StatTile
                title="Countertop Jobs"
                value={String(d.countertop_jobs_in_progress)}
                icon={<Hammer className="h-4 w-4" />}
                sub={`${d.countertop_installs_overdue} install${
                  d.countertop_installs_overdue !== 1 ? 's' : ''
                } overdue`}
                subClassName={
                  d.countertop_installs_overdue > 0
                    ? 'text-xs font-medium text-red-600 mt-0.5'
                    : 'text-xs text-muted-foreground mt-0.5'
                }
              />
            </Link>
          </div>

          {/* Work queues */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pending Pickups */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Pending Pickups</CardTitle>
                  <Link
                    to="/reports/pickups"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    View all →
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {d.pending_pickup_list.length === 0 ? (
                  <p className="px-6 pb-4 text-sm text-muted-foreground">No pending pickups.</p>
                ) : (
                  <ul className="divide-y">
                    {d.pending_pickup_list.map((p) => {
                      const badge = PICKUP_STATUS_BADGE[p.pickup_status];
                      return (
                        <li key={p.id} className="px-6 py-3 hover:bg-muted/30 transition-colors">
                          <Link to={`/estimates/${p.id}`} className="block">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-xs font-medium">{p.number}</span>
                              <Badge variant={badge.variant}>{badge.label}</Badge>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <span className="text-sm text-muted-foreground truncate">
                                {p.customer_name}
                              </span>
                              <span
                                className={`text-xs shrink-0 ${
                                  p.overdue ? 'font-medium text-amber-600' : 'text-muted-foreground'
                                }`}
                              >
                                {p.expected_pickup_date
                                  ? `${p.overdue ? 'Overdue · ' : 'Pickup '}${formatDate(
                                      p.expected_pickup_date,
                                    )}`
                                  : 'No date'}
                              </span>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Due Invoices */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Due Invoices</CardTitle>
                  <Link
                    to="/reports/ar-aging"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    View all →
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {d.due_invoices.length === 0 ? (
                  <p className="px-6 pb-4 text-sm text-muted-foreground">No invoices due.</p>
                ) : (
                  <ul className="divide-y">
                    {d.due_invoices.map((inv) => {
                      const badge = ESTIMATE_STATUS_BADGE[inv.status];
                      return (
                        <li key={inv.id} className="px-6 py-3 hover:bg-muted/30 transition-colors">
                          <Link to={`/estimates/${inv.id}`} className="block">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-xs font-medium">{inv.number}</span>
                              <Badge variant={badge.variant}>{badge.label}</Badge>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <span className="text-sm text-muted-foreground truncate">
                                {inv.customer_name}
                              </span>
                              <span className="text-sm font-medium tabular-nums shrink-0 text-destructive">
                                {formatCents(inv.balance)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatDate(inv.date)}
                            </p>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Due Purchase Orders */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Due Purchase Orders</CardTitle>
                  <Link
                    to="/reports/ap-aging"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    View all →
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {d.due_pos.length === 0 ? (
                  <p className="px-6 pb-4 text-sm text-muted-foreground">No purchase orders due.</p>
                ) : (
                  <ul className="divide-y">
                    {d.due_pos.map((po) => {
                      const badge = PO_STATUS_BADGE[po.status];
                      return (
                        <li key={po.id} className="px-6 py-3 hover:bg-muted/30 transition-colors">
                          <Link to={`/purchase-orders/${po.id}`} className="block">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-xs font-medium">{po.number}</span>
                              <Badge variant={badge.variant}>{badge.label}</Badge>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <span className="text-sm text-muted-foreground truncate">
                                {po.supplier_name}
                              </span>
                              <span className="text-sm font-medium tabular-nums shrink-0 text-destructive">
                                {formatCents(po.balance)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatDate(po.order_date)}
                            </p>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
